import { inject } from "@angular/core";
import { NavigationExtras, Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from "@ngrx/store";
import { isEqual } from "lodash";
import { catchError, concatMap, distinctUntilChanged, exhaustMap, filter, from, map, merge, of, switchMap, take, tap } from 'rxjs';

import { LOCATION_DETAILS_METADATA_KEYS } from "src/app/job/job-locations-helpers";
import { LocationDetailsWizardActions } from "src/app/shared/location-details-wizard/location-details-wizard.actions";
import { locationDetailsWizardFeature } from "src/app/shared/location-details-wizard/location-details-wizard.feature";
import { selectDistanceUnit } from "src/app/state/branding.store";
import { showGoogleMapsRouteV2 } from "src/app/utilities/locations.util";

import { CalculateDistanceGQL, UpdateJobGQL, UpdateJobInput } from "../../../../generated/graphql.generated";
import { FIELD_CONFIG } from "../../../global.constants";
import { GoogleHelperService } from "../../../services/google-helper.service";
import { PermissionService } from "../../../services/permission.service";
import { validateLocationsForEventType } from "../../job-helpers";
import { selectJob, selectJobId } from "../../job-tool.reducer";
import { WorkOrdersActions } from "../../job-workorders/job-workorders.actions";

import { JobLocationsOverviewActions } from "./overview-locations.actions";
import { overviewLocationsFeature } from "./overview-locations.feature";
import {
  selectActiveJobLocation,
  selectActiveLocationEditor,
  selectOrderedLocations,
  selectCanCalculateDistances,
  selectCalculateDistanceLocationIds,
} from "./overview-locations.selectors";

export const openDirections = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobLocationsOverviewActions.openDirectionsButtonClicked),
      concatLatestFrom(() => store.select(createSelector(
        selectOrderedLocations,
        (locations) => {
          return locations
            .map((l) => l.location?.addressLineOne)
            .filter(Boolean);
        }
      ))),
      filter(([ _action, jobLocations ]) => jobLocations.length > 1),
      map(([ _action, jobLocations ]) => showGoogleMapsRouteV2(...jobLocations)),
      tap((googleMapsRoute) => window.open(googleMapsRoute as string, '_blank')),
    );
}, { functional: true, dispatch: false });

export const deleteJobLocation = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  updateJobGQL = inject(UpdateJobGQL),
) => {
    return actions$.pipe(
      ofType(JobLocationsOverviewActions.deleteLocationButtonClicked),
      exhaustMap(() => of(null).pipe(
        concatLatestFrom(() => store.select(createSelector(
          selectJob,
          overviewLocationsFeature.selectActiveLocationType,
          (job, locationType) => {

            const location = job?.locations?.find((l) => l.locationType === locationType);

            return {
              jobId: job?.id,
              locationId: location?.locationId,
              locationType,
            };
          }
        ))),
        filter(([ _, { locationId }]) => Boolean(locationId)),
        map(([ _, { jobId, locationId, locationType }]) => {

          const updateJobs: UpdateJobInput = {
            jobId,
            removeLocations: [ locationId ],
          };

          // Clear location details for start and end location types
          if (locationType === 'start' || locationType === 'end') {

            // Clear details in metadata
            updateJobs.metadata = updateJobs.metadata || {};

            const detailKeys = Object.values(LOCATION_DETAILS_METADATA_KEYS[locationType]);

            for (const detailKey of detailKeys) {
              updateJobs.metadata[detailKey] = null;
            }

            // Clear legacy details
            const fieldConfig = FIELD_CONFIG[locationType] as typeof FIELD_CONFIG['start' | 'end'];

            const fields = Object
              .values(fieldConfig)
              .map((({ name: fieldName }) => ({ fieldName, value: null })));

            updateJobs.fields = {
              fields,
              objectLabel: 'Job',
            };
          }

          return {
            vars: { updateJobs },
            locationType
          };
        }),
        exhaustMap(({ vars, locationType }) => updateJobGQL.mutate(vars).pipe(
          map((res) => JobLocationsOverviewActions.deleteJobLocationSuccess({
            updatedJob: res.data?.updateJobs?.jobs?.[0],
            locationType,
          })),
        )),
        catchError((error) => of(JobLocationsOverviewActions.deleteJobLocationError({ error }))),
      )),
    );
  }, { functional: true, dispatch: true });

export const openJobLocationInStreetView = createEffect((
  actions$ = inject(Actions),
  googleHelper = inject(GoogleHelperService),
) => {
    return actions$.pipe(
      ofType(JobLocationsOverviewActions.openLocationInStreetViewButtonClicked),
      map(({ coordinates }) => {
        const key = googleHelper.getKey();
        const latlng = googleHelper.convertCoordinatesToLatLng(coordinates);
        const url = new URL('https://www.google.com/maps/embed/v1/');
        url.pathname += 'streetview';
        url.searchParams.set('key', key);
        url.searchParams.set('location', `${latlng.lat},${latlng.lng}`);
        return url.toString();
      }),
      tap((streetViewUrl: string) => window.open(streetViewUrl, '_blank')),
    );
  }, { functional: true, dispatch: false });

export const openJobLocationInZillow = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobLocationsOverviewActions.openLocationInZillowButtonClicked),
      concatLatestFrom(() => store.select(selectActiveJobLocation)),
      map(([ _action, jobLocation ]) => jobLocation?.location?.addressLineOne),
      filter((address) => Boolean(address)),
      map((address) => `https://www.zillow.com/homes/${address.replace(/ /g, '-')}_rb/`),
      tap((url: string) => window.open(url, '_blank')),
    );
  }, { functional: true, dispatch: false });

export const openJobLocationInGoogle = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobLocationsOverviewActions.openLocationInGoogleButtonClicked),
      concatLatestFrom(() => store.select(selectActiveLocationEditor)),
      map(([ _action, locationEditor ]) => locationEditor?.address),
      filter((address) => Boolean(address)),
      map((address) => `https://www.google.com/search?q=${encodeURIComponent(address)}`),
      tap((url: string) => window.open(url, '_blank')),
    );
  }, { functional: true, dispatch: false });

export const openJobLocationInGoogleMaps = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
    return actions$.pipe(
      ofType(JobLocationsOverviewActions.openLocationInGoogleMapsButtonClicked),
      concatLatestFrom(() => store.select(selectActiveLocationEditor)),
      map(([ _action, locationEditor ]) => locationEditor?.selectedLocation?.addressLineOne),
      filter((url) => Boolean(url)),
      map((address) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`),
      tap((url: string) => window.open(url, '_blank')),
    );
  }, { functional: true, dispatch: false });

export const watchRouteChanges = createEffect((
  store = inject(Store),
) => {

    return store.select((state: any) => state?.[overviewLocationsFeature.name]).pipe(
      filter(Boolean),
      take(1),
      switchMap(() => store.select(selectCalculateDistanceLocationIds)),
      distinctUntilChanged(isEqual),
      map((locationIds) => JobLocationsOverviewActions.locationChangesDetected({ locationIds })),
    );

  }, { functional: true, dispatch: true });

export const calculateDistanceBetweenJobLocations = createEffect((
  calculateDistancesGQL = inject(CalculateDistanceGQL),
  store = inject(Store),
  actions$ = inject(Actions),
  permissionHandler = inject(PermissionService),
) => {

    return actions$.pipe(
      ofType(JobLocationsOverviewActions.locationChangesDetected),
      // Pair the location change with the first permission emission; withLatestFrom
      // inside concatLatestFrom drops the action when watchPermission hasn't
      // emitted yet, which would leave distances empty until the next change.
      concatLatestFrom(() => [
        store.select(selectDistanceUnit),
        store.select(selectCanCalculateDistances),
      ]),
      concatMap(([ action, units, canCalculateDistances ]) => permissionHandler.watchPermission('googlemaps.calculateDistance').pipe(
        take(1),
        map((hasDistancePermission) => [ action, units, canCalculateDistances, hasDistancePermission ] as const),
      )),
      switchMap(([ { locationIds }, units, canCalculateDistances, hasDistancePermission ]) => {

        if (!canCalculateDistances || !hasDistancePermission) {
          return of(JobLocationsOverviewActions.calculateDistancesSuccess({
            calculateDistancesOutput: {
              averageTime: 0,
              estimatedTime: 0,
              totalDistance: 0,
              distanceUnits: units,
              distances: [],
            },
          }));
        }

        return calculateDistancesGQL.fetch({
          units,
          locationIds,
        }).pipe(
          map((res) => JobLocationsOverviewActions.calculateDistancesSuccess({ calculateDistancesOutput: res.data.calculateDistance })),
          catchError((error) => of(JobLocationsOverviewActions.calculateDistancesError({ error }))),
        );
      }),
    );
}, { functional: true, dispatch: true });

export const navigateToInvalidLocation = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  router = inject(Router),
) => {
  return actions$.pipe(
    ofType(WorkOrdersActions.fixLocationsLinkClicked),
    concatLatestFrom(() => store.select(createSelector(
      selectJobId,
      (jobId): { commands: any[]; extras: NavigationExtras; jobId: string } => ({
        commands: [ `/jobs/${jobId}/overview` ],
        extras: { queryParamsHandling: 'merge' },
        jobId,
      })
    ))),
    filter(([ _action, { jobId }]) => Boolean(jobId)),
    exhaustMap(([ { eventType }, { commands, extras } ]) => from(router.navigate(commands, extras)).pipe(
      catchError(() => of(false)),
      filter((navigationSuccess) => navigationSuccess),
      concatLatestFrom(() => store.select(createSelector(
        selectJob,
        (job) => {

          const validationResult = validateLocationsForEventType(
            job?.locations,
            eventType
          );

          const locationType = validationResult.missingLocations[0] ?? validationResult.invalidLocations[0];

          return {
            locationType,
            existingLocation: job?.locations?.find((l) => l.locationType === locationType)?.location,
          };
        }
      ))),
      filter(([_, payload]) => Boolean(payload.locationType)),
      map(([ _action, payload ]) => JobLocationsOverviewActions.navigateToInvalidLocationSuccess(payload)),
    )),
  );
}, { functional: true, dispatch: true });

export const loadWizardDetails = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {

    const stepsCompleted$ = actions$.pipe(
      ofType(LocationDetailsWizardActions.optionClicked),
      concatLatestFrom(() => [
        store.select(locationDetailsWizardFeature.selectStep),
      ]),
      filter(([ _, step ]) => !step),
      map(([ action ]) => action),
    );

    const exitDialogClicked$ = actions$.pipe(
      ofType(
        JobLocationsOverviewActions.exitWizardButtonClicked,
      ),
    );

    return merge(stepsCompleted$, exitDialogClicked$).pipe(
      concatLatestFrom(() => [
        store.select(locationDetailsWizardFeature.selectLocationType),
        store.select(locationDetailsWizardFeature.selectAnswers),
      ]),
      map(([ action, locationType, answers ]) => JobLocationsOverviewActions.wizardDetailsLoaded({
        completed: action.type === LocationDetailsWizardActions.optionClicked.type,
        locationType,
        buildingType: answers.buildingType,
        size: answers.size,
        access: answers.access,
        elevatorType: answers.elevatorType,
        floor: answers.floor,
      })),
    );
}, { functional: true, dispatch: true });
